import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { DealActions, NegotiationFieldMeta } from 'api'
import { ModalContent, ModalFooter } from 'view/components/panels'
import { DialogField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Locale, Translation } from 'locale'
import { ModalWindow, Table } from 'view/components/panels'
import { useOnMount, useUserPreference } from 'view/hooks'
import { createDealDetailsByIdsSelector } from 'selectors/dealSelectors'
import { bulkCloseOpenOptions } from './ServicingBulkCloseOpenDialog/BulkCloseOpenGridModel'
import { useBulkCancelColumns } from './BulkCancelGridModel'

interface ServicingTraderCancelDialogProps {
  dealIds: string[]
  isAdmin: boolean
  onClose?: () => unknown
}

const Content = styled(ModalContent)`
  flex-direction: row;
  width: 800px;
`

export const ServicingTraderBulkCancelDialog: React.FC<ServicingTraderCancelDialogProps> = ({ dealIds, onClose, isAdmin }) => {
  const dispatch = useDispatch()
  const userPreference = useUserPreference()

  const getDealDetailsById = useSelector(createDealDetailsByIdsSelector)
  const dealDetails = useMemo(() => getDealDetailsById(dealIds), [dealIds, getDealDetailsById])

  useOnMount(() => {
    dispatch(
      DealActions.servicingBulkCancel(dealIds, isAdmin ? 'DealServicingStartAdminCancelEvent' : 'DealServicingStartTraderCancelEvent')
    )
  })

  const handleSend = () => {
    dispatch(DealActions.servicingBulkCancel(dealIds, isAdmin ? 'DealServicingAcceptEvent' : 'DealServicingSendEvent'))
    onClose?.()
  }

  const handleCancel = () => {
    dispatch(DealActions.servicingBulkCancel(dealIds, 'DealServicingCancelEvent'))
    onClose?.()
  }
  const handleUpdateField = useCallback(
    ({ dealId, value, field, rawValue }: { dealId: string; value: any; field: NegotiationFieldMeta; rawValue?: any }) => {
      dispatch(
        DealActions.servicingUpdateAttributes({
          dealId,
          field,
          updatedValue: value,
          updatedRawValue: rawValue,
          decisionMakerOrientedByNegotiationSide: true,
          meta: { haircutSign: userPreference.haircutSign },
          revertFirst: false
        })
      )
    },
    [dispatch, userPreference.haircutSign]
  )

  const columns = useBulkCancelColumns(handleUpdateField)

  return (
    <ModalWindow title={Locale.translate(Translation.TC_CANCEL_DEAL)} onEscKey={handleCancel}>
      <Content>
        <DialogField>
          <Table columns={columns} rows={dealDetails} options={bulkCloseOpenOptions} />
        </DialogField>
      </Content>
      <ModalFooter>
        <TextButton type={TextButtonType.Tertiary} onClick={handleCancel}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton type={TextButtonType.Primary} onClick={handleSend}>
          {Locale.translate(Translation.TC_BUTTON_SEND_REQUEST)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}
